// oxlint-disable jsx-no-new-object-as-prop

import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, m } from "motion/react";
import { useCallback, useMemo, useState } from "react";

import { useGlobal } from "../../lib/global-context";
import { initiativesQuery } from "../../lib/queries";
import type { Category as CategoryType, VoidComponent } from "../../lib/types";
import { booleanAttribute } from "../../lib/utils";
import { Icon } from "../icon";
import { transitionAnimate } from "./const";
import { Initiative } from "./initiative";

const Category: VoidComponent<CategoryType & { pillar: string }> = (props) => {
	const { exploreInitiativesTitle } = useGlobal();
	const [isOpen, setIsOpen] = useState(false);

	const query = useQuery(initiativesQuery);
	const initiatives = useMemo(
		() =>
			query.data?.filter((initiative) =>
				props.initiatives.some((i) => i._ref === initiative._id),
			) ?? [],
		[query.data, props.initiatives],
	);

	const handleToggle = useCallback(() => {
		setIsOpen((open) => !open);
	}, []);

	return (
		<m.li
			className="group mb-6.5 flex flex-col gap-7.5 text-foreground"
			data-is-open={booleanAttribute(isOpen)}
			variants={{
				initial: { opacity: 0, y: "1rem" },
				animate: { opacity: 1, y: "0rem", transition: transitionAnimate },
				exit: { opacity: 0, y: "1rem" },
			}}
		>
			<button
				type="button"
				onClick={handleToggle}
				className="progress-link flex cursor-pointer justify-between gap-4 pt-3 group-data-is-open:[--progress:1]"
			>
				<h2 className="text-xl">{props.title}</h2>

				<Icon name={isOpen ? "subtract" : "add"} className="size-6" />
			</button>

			<div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-400 ease-out-quart group-data-is-open:grid-rows-[1fr]">
				<div className="overflow-hidden">
					<AnimatePresence>
						{isOpen && (
							<m.div
								variants={{
									initial: { opacity: 0, y: "1rem" },
									animate: { opacity: 1, y: "0rem" },
									exit: { opacity: 0, y: "1rem" },
								}}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{ type: "spring", visualDuration: 0.4, bounce: 0.1 }}
							>
								<p className="mb-19 font-serif text-sm desktop:text-xl">{props.description}</p>

								<p className="mb-3 text-foreground opacity-50">{exploreInitiativesTitle}</p>

								<ul className="flex flex-col counter-reset">
									{initiatives.map((initiative) => (
										<Initiative key={initiative._id} {...initiative} />
									))}
								</ul>
							</m.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		</m.li>
	);
};

export { Category };
